import { useEffect, useMemo, useRef, useState } from "react";

interface Locality {
  name: string;
  lat: number;
  lng: number;
  base?: boolean;
}

const CENTER = { lat: 12.9716, lng: 77.5946 };
const ZOOM = 11;
const TILE = 256;

const MAP_SRC =
  "https://www.google.com/maps?q=Bengaluru,Karnataka,India&z=11&output=embed&t=m";

const LOCALITIES: Locality[] = [
  { name: "Kannahalli", lat: 12.9805, lng: 77.4435, base: true },
  { name: "Anikethana Nagara", lat: 12.9822, lng: 77.4444 },
  { name: "Nagarabhavi", lat: 12.9556, lng: 77.5003 },
  { name: "Rajajinagar", lat: 12.9916, lng: 77.5527 },
  { name: "Malleswaram", lat: 13.0035, lng: 77.5649 },
  { name: "Jayanagar", lat: 12.9308, lng: 77.5838 },
  { name: "JP Nagar", lat: 12.9063, lng: 77.5857 },
  { name: "HSR Layout", lat: 12.9116, lng: 77.6412 },
  { name: "Whitefield", lat: 12.9698, lng: 77.75 },
  { name: "Yelahanka", lat: 13.1007, lng: 77.5963 },
  { name: "Magadi Road", lat: 12.987, lng: 77.508 },
  { name: "NICE Road", lat: 12.912, lng: 77.47 },
  { name: "Seegehalli", lat: 13.03, lng: 77.57 },
  { name: "Giddenahalli", lat: 13.01, lng: 77.42 },
];

function project(lat: number, lng: number) {
  const size = TILE * 2 ** ZOOM;
  const x = ((lng + 180) / 360) * size;
  const sinLat = Math.sin((lat * Math.PI) / 180);
  const y = (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * size;
  return { x, y };
}

function CoverageMapCanvas({ fill = false }: { fill?: boolean }) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const update = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const pins = useMemo(() => {
    const center = project(CENTER.lat, CENTER.lng);
    return LOCALITIES.map((item) => {
      const point = project(item.lat, item.lng);
      const left = size.w ? 50 + ((point.x - center.x) / size.w) * 100 : 50;
      const top = size.h ? 50 + ((point.y - center.y) / size.h) * 100 : 50;
      return { ...item, left, top };
    });
  }, [size.w, size.h]);

  const visiblePins = pins.filter((pin) => pin.left >= 4 && pin.left <= 96 && pin.top >= 6 && pin.top <= 96);

  return (
    <div
      className={`relative overflow-hidden rounded-lg border border-background-200 bg-background-100 ${
        fill ? "flex h-full min-h-[320px] flex-col" : ""
      }`}
    >
      <div ref={boxRef} className={fill ? "relative w-full flex-1" : "relative h-[320px] w-full md:h-[420px]"}>
        <iframe
          title="Google map of Bangalore showing the residential localities served by Lahari Packers and Movers"
          src={MAP_SRC}
          className="h-full w-full"
          allowFullScreen
          loading="lazy"
          referrerPolicy="strict-origin-when-cross-origin"
        />
        {size.w > 0 &&
          visiblePins.map((pin) => (
            <div
              key={pin.name}
              className="pointer-events-none absolute -translate-x-1/2 -translate-y-full"
              style={{ left: `${pin.left}%`, top: `${pin.top}%` }}
            >
              <div className="flex flex-col items-center">
                <span
                  className={`mb-0.5 whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-semibold backdrop-blur-sm ${
                    pin.base
                      ? "border-accent-300 bg-accent-500 text-background-50"
                      : "border-background-300 bg-background-50/95 text-foreground-900"
                  }`}
                >
                  {pin.name}
                </span>
                <i
                  className={`${pin.base ? "ri-map-pin-2-fill text-accent-600" : "ri-map-pin-2-fill text-primary-600"} text-2xl leading-none`}
                  aria-hidden="true"
                />
              </div>
            </div>
          ))}
      </div>
      <div className="absolute inset-x-3 bottom-3 flex flex-wrap items-center justify-between gap-3 rounded-md border border-background-200 bg-background-50/95 px-3 py-2.5 backdrop-blur-sm md:inset-x-4 md:bottom-4">
        <span className="inline-flex items-center gap-2 text-xs font-semibold text-foreground-950 md:text-sm">
          <i className="ri-map-pin-range-line text-lg text-primary-600" aria-hidden="true" /> Coverage across all of
          Bangalore
        </span>
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground-600">
          <i className="ri-flashlight-fill text-accent-500" aria-hidden="true" /> Same-day local moves
        </span>
      </div>
    </div>
  );
}

export function CoverageLocalitiesList({ className = "" }: { className?: string }) {
  return (
    <div className={`rounded-lg border border-background-200 bg-background-50 p-6 ${className}`}>
      <h3 className="font-heading text-base font-bold text-foreground-950">Our most-covered localities</h3>
      <p className="mt-2 text-sm leading-relaxed text-foreground-600">
        These are the Bangalore pockets our local shifting crews move through most often.
      </p>
      <ul className="mt-5 grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
        {LOCALITIES.map((item) => (
          <li
            key={item.name}
            className="flex min-w-0 items-center gap-2.5 rounded-md border border-background-200 bg-background-100 px-3 py-2 text-sm text-foreground-700"
          >
            <span
              className={`flex h-5 w-5 shrink-0 items-center justify-center ${
                item.base ? "text-accent-600" : "text-primary-600"
              }`}
            >
              <i className="ri-map-pin-2-fill text-base" aria-hidden="true" />
            </span>
            <span className="min-w-0 truncate">{item.base ? `${item.name} (Hub)` : item.name}</span>
          </li>
        ))}
      </ul>
      <div className="mt-5 flex items-center gap-2.5 rounded-md border border-background-200 bg-background-100 px-3.5 py-3">
        <i className="ri-route-line text-xl text-accent-500" aria-hidden="true" />
        <p className="text-xs leading-relaxed text-foreground-600">
          Plus every other major residential area in Bangalore — 21 localities and counting.
        </p>
      </div>
    </div>
  );
}

export default function BangaloreCoverageMap({
  showLocalities = true,
  fillMapHeight = false,
}: {
  showLocalities?: boolean;
  fillMapHeight?: boolean;
}) {
  if (!showLocalities) {
    return <CoverageMapCanvas fill={fillMapHeight} />;
  }
  return (
    <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-[1.55fr_1fr]">
      <CoverageMapCanvas />
      <CoverageLocalitiesList />
    </div>
  );
}